import { Component, computed, inject, input, output, signal } from "@angular/core";
import { MessagingService } from "@services/messaging.service";
import { AuthState } from "@states/auth.state";

@Component({
  selector: "app-settings-modal",
  standalone: true,
  host: {
    "(document:fullscreenchange)": "onFullscreenChange()",
    "(document:webkitfullscreenchange)": "onFullscreenChange()",
    "(document:mozfullscreenchange)": "onFullscreenChange()",
    "(document:MSFullscreenChange)": "onFullscreenChange()",
  },
  template: `
    @if (isOpen()) {
      <div class="backdrop" (click)="closed.emit()"></div>
      <div class="settings-modal">
        <div class="settings-modal__header">
          <h2 class="title-h4 settings-modal__title">Paramètres</h2>
          <button class="btn-icon btn-primary settings-modal__close" (click)="closed.emit()">
            <img src="assets/icons/close.svg" alt="Fermer" />
          </button>
        </div>

        <ul class="settings-modal__list">
          <li>
            <button type="button" class="btn btn-secondary settings-modal__item" (click)="openPanel.emit()">
              <span>Modifier mon curseur</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#152942" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </li>
          @if (isAndroidMobile) {
            <li>
              <button type="button" class="btn btn-secondary settings-modal__item" (click)="toggleFullscreen()">
                <span>{{ fullscreenLabel() }}</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3"
                    stroke="#152942"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </li>
          }
          @if (isLoggedIn()) {
            <li>
              <button type="button" class="btn btn-secondary settings-modal__item" (click)="sendAccountSettingsMessage()">
                <span>Paramètres du compte</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                    stroke="#152942"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </li>
            <li>
              <button type="button" class="btn btn-secondary settings-modal__item" (click)="sendLogoutMessage()">
                <span>Déconnexion</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                    stroke="#152942"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </li>
          }
        </ul>
      </div>
    }
  `,
  styles: `
    @use "responsive" as *;

    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 6000;
    }

    .settings-modal {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      max-width: 480px;
      background: var(--color-white);
      border-radius: 24px;
      border: 3px solid var(--color-cream);
      z-index: 6001;
      padding: 32px 24px 24px;
    }

    .settings-modal__header {
      margin-bottom: 24px;
      text-align: center;

      @include screenWidth("mobile-only") {
        text-align: left;
      }
    }

    .settings-modal__title {
      margin: 0;
    }

    .settings-modal__close {
      position: absolute;
      top: -25px;
      right: -25px;

      @include screenWidth("mobile-only") {
        top: 5px;
        right: 5px;
      }
    }

    .settings-modal__list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .settings-modal__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 16px 24px;

      @include screenWidth("mobile-only") {
        font-size: 1.4rem;
        padding: 12px 16px;
      }
    }
  `,
})
export class SettingsModal {
  private authState = inject(AuthState);
  private messagingService = inject(MessagingService);

  isOpen = input.required<boolean>();
  closed = output<void>();
  openPanel = output<void>();

  protected isLoggedIn = () => !!this.authState.currentUser();
  protected isAndroidMobile = /Android/i.test(navigator.userAgent);
  protected isFullscreen = signal(!!document.fullscreenElement);
  protected fullscreenLabel = computed(() => (this.isFullscreen() ? "Quitter le mode plein écran" : "Passer en plein écran"));

  protected async toggleFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }

    await document.documentElement.requestFullscreen();
  }

  protected onFullscreenChange() {
    this.isFullscreen.set(!!document.fullscreenElement);
  }

  sendAccountSettingsMessage() {
    this.messagingService.sendAccountSettingsMessage();
  }

  sendLogoutMessage() {
    this.messagingService.sendLogoutMessage();
  }
}
